import { supabase } from '../supabase';

/**
 * The AI Lab talks to the same edge function the app does, `generate-cards`,
 * with one difference: it names the model. Everything else — vibe, depth,
 * game mode, language — is what a player would have chosen on the phone, so
 * a run here is exactly what that player would have got.
 */

export interface LabModel {
  id: string;
  label: string;
  maker: string;
  tier: 'medium' | 'large';
  provider: string;
  /** EUR per million tokens, [input, output]. */
  price: [number, number];
  isDefault: boolean;
}

const SELECTED = 'lab.models';

/** Which models the last comparison ran on; the two the app started with
 *  until you choose otherwise. Unknown ids (a model since removed) drop out. */
export function loadSelection(models: LabModel[]): string[] {
  let stored: string[] | null = null;
  try { stored = JSON.parse(localStorage.getItem(SELECTED) ?? 'null'); } catch { /* ignore */ }
  const known = new Set(models.map((m) => m.id));
  const kept = (stored ?? []).filter((id) => known.has(id));
  if (kept.length) return kept;
  return models.filter((m) => m.id === 'ministral-14b' || m.id === 'gemma-4').map((m) => m.id);
}

export function saveSelection(ids: string[]) {
  try { localStorage.setItem(SELECTED, JSON.stringify(ids)); } catch { /* ignore */ }
}

/** Rough cost of one generation on a model: ~1.6k tokens in, ~1.2k out. */
export function estimateRunCost(model: LabModel): number {
  return (model.price[0] * 1600 + model.price[1] * 1200) / 1_000_000;
}

/** The five positions of the app's vibe slider, in order. */
export const VIBES = ['Family Friendly', 'Fun', 'Balanced', 'Roast', 'Unhinged'] as const;

/** The five positions of the depth slider, with the exact sentence the app
 *  sends for each — copied from CardDepth in CouchFiestaCore. */
export const DEPTHS = [
  { label: 'Rapid-fire', instruction: 'Generate very short, rapid-fire questions that can be answered in a few seconds. Keep them punchy and simple.' },
  { label: 'Short', instruction: 'Generate short, fun questions that are easy to answer quickly — a sentence or two at most.' },
  { label: 'Balanced', instruction: 'Generate a balanced mix of quick questions and ones that might spark a bit of conversation.' },
  { label: 'Conversational', instruction: 'Generate thought-provoking questions that spark real conversation and stories. Players should talk for 30-60 seconds.' },
  { label: 'Deep', instruction: 'Generate deep, open-ended questions that lead to extended conversations and storytelling. Each card should keep players talking for a minute or two.' },
] as const;

/** Language codes as the app sends them: the bare language, not the locale. */
export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pt', label: 'Português' },
  { code: 'zh', label: '中文' },
] as const;

export type GameMode = 'personalized' | 'general';

export type Drinks = 'auto' | 'yes' | 'light' | 'none';

export interface LabInput {
  groupDescription: string;
  vibe: number;
  depth: number;
  gameMode: GameMode;
  language: string;
  /** Overrides the drinking the vibe implies; 'auto' leaves it to the vibe (what the sliders do). */
  drinks?: Drinks;
  /**
   * A system prompt of your own, sent instead of the one the app composes
   * from the settings above. Unset means "the app's prompt", exactly.
   */
  systemPrompt?: string;
}

/** The system prompt the app would compose for these settings — the
 *  starting point for editing one. */
export async function fetchDefaultPrompt(input: LabInput): Promise<string> {
  const params = new URLSearchParams({
    vibe: String(input.vibe),
    gameMode: input.gameMode,
    cardDepthInstruction: DEPTHS[input.depth]?.instruction ?? '',
    language: input.language,
    ...(input.drinks && input.drinks !== 'auto' ? { drinks: input.drinks } : {}),
  });
  const { data, error } = await supabase.functions.invoke<{ system: string }>(
    `generate-cards/prompt?${params}`, { method: 'GET' },
  );
  if (error) throw error;
  return data?.system ?? '';
}

export interface LabCard {
  question: string;
  instruction: string;
}

/** What one model gave back for one input, or why it didn't. */
export interface LabResult {
  model: string;
  status: 'running' | 'ok' | 'failed';
  ms: number;
  subtitle?: string;
  cards?: LabCard[];
  error?: string;
}

/** One press of Run: the input and every model's answer to it. */
export interface LabRun {
  id: string;
  at: string;
  input: LabInput;
  results: LabResult[];
}

export async function fetchLabModels(): Promise<LabModel[]> {
  const { data, error } = await supabase.functions.invoke<{ models: LabModel[] }>(
    'generate-cards/models', { method: 'GET' },
  );
  if (error) throw error;
  return data?.models ?? [];
}

/** Runs one model. Never throws: a failure is a result too, and the point of
 *  the lab is to see failures next to successes. */
export async function generateWith(model: string, input: LabInput): Promise<LabResult> {
  const started = performance.now();
  const body = {
    groupDescription: input.groupDescription,
    vibe: input.vibe,
    gameMode: input.gameMode,
    cardDepthInstruction: DEPTHS[input.depth]?.instruction ?? '',
    language: input.language,
    model,
    ...(input.systemPrompt ? { systemPrompt: input.systemPrompt } : {}),
  };
  try {
    const { data, error } = await supabase.functions.invoke<{
      subtitle: string; cards: LabCard[]; model: string; error?: string; detail?: string;
    }>('generate-cards', { body });
    const ms = Math.round(performance.now() - started);
    if (error) {
      // The function answers errors as JSON with a message worth showing.
      const detail = await describeFunctionError(error);
      return { model, status: 'failed', ms, error: detail };
    }
    if (!data || data.error) {
      return { model, status: 'failed', ms, error: data?.detail ?? data?.error ?? 'Empty response' };
    }
    return { model, status: 'ok', ms, subtitle: data.subtitle, cards: data.cards };
  } catch (e) {
    return { model, status: 'failed', ms: Math.round(performance.now() - started), error: String(e) };
  }
}

async function describeFunctionError(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx instanceof Response) {
    try {
      // The function sends the provider's own words as `detail` next to the
      // message the app shows; here the detail is the useful half.
      const body = await ctx.json() as { error?: string; detail?: string };
      if (body?.detail) return `${ctx.status}: ${body.detail}`;
      if (body?.error) return `${ctx.status}: ${body.error}`;
    } catch { /* not JSON */ }
    return `HTTP ${ctx.status}`;
  }
  return error instanceof Error ? error.message : String(error);
}

/* ------------------------------------------------------------- checks */

export type Severity = 'crit' | 'warn';

/** Anything in braces that isn't one of the two the engine knows. */
export const UNKNOWN_PLACEHOLDER = /\{(?!PLAYER\}|OTHER_PLAYER\})[^{}]*\}/;
export interface Issue { severity: Severity; text: string }

/**
 * What the app would silently suffer from. These are the same rules the
 * prompt states, checked mechanically — the first thing to compare between
 * models before anyone reads a single card.
 */
export function checkResult(result: LabResult, _input: LabInput): Issue[] {
  if (result.status !== 'ok' || !result.cards) return [];
  const issues: Issue[] = [];
  const cards = result.cards;

  if (cards.length !== 18) issues.push({ severity: 'warn', text: `${cards.length} cards, asked for exactly 18` });

  // A card that names only {OTHER_PLAYER}, or names nobody, is fine: the
  // engine makes the one person named the current player. Not flagged.

  const unknown = cards.filter((c) => UNKNOWN_PLACEHOLDER.test(c.question + c.instruction)).length;
  if (unknown) issues.push({ severity: 'crit', text: `${unknown} card${unknown > 1 ? 's' : ''} with an unknown placeholder — shown as braces` });

  const blank = cards.filter((c) => !c.instruction.trim()).length;
  if (blank) issues.push({ severity: 'warn', text: `${blank} card${blank > 1 ? 's' : ''} with no instruction` });

  const seen = new Set<string>();
  let dupes = 0;
  for (const c of cards) {
    const k = c.question.trim().toLowerCase();
    if (seen.has(k)) dupes++; else seen.add(k);
  }
  if (dupes) issues.push({ severity: 'warn', text: `${dupes} duplicate question${dupes > 1 ? 's' : ''}` });

  if (!result.subtitle?.trim()) issues.push({ severity: 'warn', text: 'No subtitle' });
  else if (result.subtitle.trim().split(/\s+/).length > 6) issues.push({ severity: 'warn', text: 'Subtitle longer than 6 words' });

  return issues;
}

/* ------------------------------------------------------------ history */

const RUNS = 'lab.runs';
const KEEP = 20;

/** Recent runs, newest first, kept in this browser only. Enough to come back
 *  to a comparison; not a dataset — that comes when we know what to measure. */
export function loadRuns(): LabRun[] {
  try { return JSON.parse(localStorage.getItem(RUNS) ?? '[]') as LabRun[]; } catch { return []; }
}

export function saveRun(run: LabRun) {
  const runs = [run, ...loadRuns().filter((r) => r.id !== run.id)].slice(0, KEEP);
  try { localStorage.setItem(RUNS, JSON.stringify(runs)); } catch { /* full or private */ }
}

export function clearRuns() {
  try { localStorage.removeItem(RUNS); } catch { /* ignore */ }
}

/* ------------------------------------------------------------ feedback */

export type Verdict = 'up' | 'down';

export interface ModelScore {
  model: string;
  ups: number;
  downs: number;
  total: number;
  /** ups / total, 0–1. */
  score: number;
  last_at: string;
}

/** A model needs this many verdicts before its score means anything. */
export const MIN_FEEDBACK = 10;

/**
 * Record what you thought of one model's answer. The whole generation goes
 * with it, so the verdict keeps its context. `null` withdraws a verdict.
 */
export async function sendVerdict(
  meId: string, run: LabRun, result: LabResult, verdict: Verdict | null,
): Promise<void> {
  if (verdict === null) {
    const { error } = await supabase.from('ai_lab_feedback')
      .delete().eq('member_id', meId).eq('run_id', run.id).eq('model', result.model);
    if (error) throw error;
    return;
  }
  const { error } = await supabase.from('ai_lab_feedback').upsert({
    member_id: meId,
    run_id: run.id,
    model: result.model,
    verdict,
    input: run.input,
    output: { subtitle: result.subtitle ?? '', cards: result.cards ?? [] },
    latency_ms: result.ms,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'member_id,run_id,model' });
  if (error) throw error;
}

/** Your own verdicts on one run, by model. */
export async function fetchVerdicts(meId: string, runId: string): Promise<Record<string, Verdict>> {
  const { data, error } = await supabase.from('ai_lab_feedback')
    .select('model, verdict').eq('member_id', meId).eq('run_id', runId);
  if (error) throw error;
  return Object.fromEntries((data ?? []).map((r) => [r.model as string, r.verdict as Verdict]));
}

/** Every model's tally, from everyone's verdicts. */
export async function fetchModelScores(): Promise<ModelScore[]> {
  const { data, error } = await supabase.from('ai_lab_model_score').select('*');
  if (error) throw error;
  return (data ?? []).map((r) => ({
    model: r.model as string,
    ups: Number(r.ups), downs: Number(r.downs), total: Number(r.total),
    score: Number(r.score), last_at: r.last_at as string,
  }));
}
